package com.evoapps.evoshift.ui.adaptive

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.DeepNavy

/*
 * SH-169: scrollbars for the mouse (the large-screen checklist's Scrollbar_Display): the desktop
 * app and (SH-208) the web draw Compose's scrollbars, draggable and clickable on the track;
 * SH-161: Android draws a thin one of its own while a mouse or a trackpad is used, none for touch
 * (iOS none: its own scrolling indicators).
 */

/** Test tags of the scrollbars where they are drawn. */
object ScrollbarTags {
    const val VERTICAL = "scrollbar_vertical"
    const val HORIZONTAL = "scrollbar_horizontal"
}

/**
 * SH-208: the scrollbars' look, from the appearance's base colour (SH-196) on the cream screens:
 * the thumb at 55 % reaches 3:1 against cream (non-text contrast), stronger under the mouse, on a
 * faint track that shows where the bar is.
 */
object ScrollbarLook {
    val Thickness = 10.dp
    val Thumb: Color get() = DeepNavy.copy(alpha = 0.55f)
    val ThumbHovered: Color get() = DeepNavy.copy(alpha = 0.85f)
    val Track: Color get() = DeepNavy.copy(alpha = 0.08f)
}

/** A vertical scrollbar for [state] along the right edge of its list (nothing where none is drawn). */
@Composable
expect fun ListVerticalScrollbar(state: LazyListState, modifier: Modifier = Modifier)

/** A horizontal scrollbar for [state] along the bottom of its row (nothing where none is drawn). */
@Composable
expect fun RowHorizontalScrollbar(state: ScrollState, modifier: Modifier = Modifier)

/**
 * SH-208: a row that scrolls sideways (chips, a week's days), with its scrollbar under it where
 * one is drawn and only while the row does not fit. [contentPadding] is inside the scrolling.
 */
@Composable
fun ScrollingRow(
    modifier: Modifier = Modifier,
    horizontalArrangement: Arrangement.Horizontal = Arrangement.Start,
    verticalAlignment: Alignment.Vertical = Alignment.Top,
    contentPadding: PaddingValues = PaddingValues(),
    content: @Composable RowScope.() -> Unit
) {
    val scroll = rememberScrollState()
    Column(modifier) {
        Row(
            modifier = Modifier.fillMaxWidth().horizontalScroll(scroll).padding(contentPadding),
            horizontalArrangement = horizontalArrangement,
            verticalAlignment = verticalAlignment,
            content = content
        )
        RowHorizontalScrollbar(scroll, Modifier.fillMaxWidth().padding(top = 4.dp))
    }
}
